import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'

import Link from 'next/link'
import { ArrowRight, Clock, Globe, Languages, MapPin } from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { ContactForm } from '@/components/site/contact-form'
import { CtaBand, PageHero } from '@/components/site/primitives'
import { useLocale } from '@/i18n/use-locale'
import { siteContact } from '@/i18n/site-contact'

const detailIcons = [Clock, MapPin, Languages, Globe]

const ContactPage: FC = (): ReactNode => {
    const locale = useLocale()
    const copy = siteContact[locale]

    return (
        <Layout>
            <Seo
                title={copy.seoTitle}
                description={copy.seoDescription}
                path='/contact'
                breadcrumbs={[{ name: copy.breadcrumb, path: '/contact' }]}
            />

            <PageHero eyebrow={copy.hero.eyebrow} title={copy.hero.title} text={copy.hero.text} />

            <section id='contact-form' className='bg-background py-20 sm:py-24'>
                <div className='container-page grid gap-10 lg:grid-cols-[1.5fr_1fr] lg:gap-14'>
                    <div>
                        <ContactForm />
                    </div>

                    <aside className='space-y-5'>
                        <div className='rounded-3xl bg-ink p-7 text-white sm:p-8'>
                            <h2 className='text-xs font-semibold uppercase tracking-[0.18em] text-white/50'>
                                {copy.next.title}
                            </h2>

                            <ol className='mt-6 space-y-6'>
                                {copy.next.steps.map((step, index) => (
                                    <li key={step.title} className='flex gap-4'>
                                        <span className='flex size-9 shrink-0 items-center justify-center rounded-full border border-white/20 font-mono text-xs'>
                                            {String(index + 1).padStart(2, '0')}
                                        </span>

                                        <div>
                                            <p className='font-semibold'>{step.title}</p>
                                            <p className='mt-1 text-sm leading-relaxed text-white/65'>{step.text}</p>
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>

                        <dl className='divide-y divide-border rounded-3xl border border-border bg-card'>
                            {copy.details.map((item, index) => {
                                const Icon = detailIcons[index] ?? Globe

                                return (
                                    <div key={item.label} className='flex items-center gap-4 px-6 py-4'>
                                        <Icon className='size-5 shrink-0 text-primary' aria-hidden='true' />

                                        <div>
                                            <dt className='text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground'>{item.label}</dt>
                                            <dd className='mt-0.5 font-medium'>{item.value}</dd>
                                        </div>
                                    </div>
                                )
                            })}
                        </dl>

                        <div className='rounded-3xl bg-mist p-6'>
                            <h2 className='font-semibold'>{copy.portal.title}</h2>
                            <p className='mt-1 text-sm leading-relaxed text-muted-foreground'>{copy.portal.text}</p>

                            <Link href='/portal/login' className='group mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary'>
                                {copy.portal.link}
                                <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                            </Link>
                        </div>
                    </aside>
                </div>
            </section>

            <CtaBand title={copy.ctaTitle} text={copy.ctaText} />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps = async () => ({ props: {} })

export default ContactPage
